import { Button, Space } from 'antd'
import type { TableColumnsType } from 'antd'
import { EditOutlined, EyeOutlined, PlayCircleOutlined, StopOutlined } from '@ant-design/icons'
import type { TFunction } from 'i18next'

import { localeService } from '../../../services/localeService'
import type { ValidationRule } from '../../../types/modelValidation'
import { ValidationAssetStatusTag } from './ValidationAssetStatusTag'

interface ValidationRuleColumnsOptions {
    canActivate: boolean
    onOpen: (rule: ValidationRule) => void
    onSetDisabled: (rule: ValidationRule, disabled: boolean) => void
    t: TFunction
}

export const buildValidationRuleColumns = ({
    canActivate,
    onOpen,
    onSetDisabled,
    t,
}: ValidationRuleColumnsOptions): TableColumnsType<ValidationRule> => [
    {
        title: t('admin:modelValidation.columns.name'),
        dataIndex: 'name',
        ellipsis: true,
        width: 160,
    },
    {
        title: t('admin:modelValidation.columns.description'),
        dataIndex: 'description',
        ellipsis: { showTitle: false },
        render: (value?: string | null) => value || '-',
    },
    {
        title: t('admin:modelValidation.columns.status'),
        width: 90,
        render: (_, record) => <ValidationAssetStatusTag status={record.status} />,
    },
    {
        title: t('admin:modelValidation.columns.createdAt'),
        dataIndex: 'created_at',
        render: (value: string) => localeService.formatDateTime(value),
        width: 160,
    },
    {
        title: t('admin:modelValidation.columns.actions'),
        fixed: 'right',
        width: 180,
        render: (_, record) => {
            const editable = canActivate
            const action = editable ? 'edit' : 'view'
            const lifecycleEditable = editable && ['published', 'disabled'].includes(record.status)
            const disabled = record.status === 'published'
            return (
                <Space size="small">
                    <Button
                        aria-label={t(`admin:modelValidation.actions.${action}`)}
                        icon={editable ? <EditOutlined /> : <EyeOutlined />}
                        onClick={() => onOpen(record)}
                        size="small"
                        type="link"
                    >
                        {t(`admin:modelValidation.actions.${action}`)}
                    </Button>
                    {lifecycleEditable ? (
                        <Button
                            aria-label={t(
                                `admin:modelValidation.actions.${disabled ? 'disable' : 'restore'}`
                            )}
                            danger={disabled}
                            icon={disabled ? <StopOutlined /> : <PlayCircleOutlined />}
                            onClick={() => onSetDisabled(record, disabled)}
                            size="small"
                            type="link"
                        >
                            {t(`admin:modelValidation.actions.${disabled ? 'disable' : 'restore'}`)}
                        </Button>
                    ) : null}
                </Space>
            )
        },
    },
]
